import { Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Space, Switch, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type TransportRoute = {
  id: string;
  code: string;
  name: string;
  capacity?: number | null;
  vehicle_label?: string | null;
  is_active?: number | boolean;
  stop_count?: number;
  active_riders?: number;
};

type RouteForm = {
  code: string;
  name: string;
  capacity?: number | null;
  vehicle_label?: string;
  is_active?: boolean;
};

export default function TransportRoutesPage() {
  const { t } = useTranslation('education');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.transport.manage') ||
      user?.permissions?.includes('education.admin')
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm<RouteForm>();

  const list = useQuery({
    queryKey: ['education-transport-routes'],
    queryFn: () => api.get<TransportRoute[]>('/education/transport/routes'),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['education-transport-routes'] });

  const create = useMutation({
    mutationFn: (values: RouteForm) => api.post<{ route: { id: string } }>('/education/transport/routes', values),
    onSuccess: (result) => {
      message.success(t('transportRouteCreated'));
      setCreateOpen(false);
      form.resetFields();
      refresh();
      const id = (result.data as { route?: { id?: string } })?.route?.id;
      if (id) navigate(`/education/transport/routes/${id}`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/education/transport/routes/${id}`),
    onSuccess: () => {
      message.success(t('transportRouteDeleted'));
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('transportRoutes')}
      description={t('transportRoutesIntro')}
      extra={
        <Space>
          {canManage ? (
            <Button
              type="primary"
              onClick={() => {
                form.setFieldsValue({ is_active: true });
                setCreateOpen(true);
              }}
            >
              {t('newTransportRoute')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/transport')}>{t('backToTransport')}</Button>
        </Space>
      }
    >
      <Card size="small">
        <Table
          rowKey="id"
          size="small"
          loading={list.isLoading}
          dataSource={list.data?.data ?? []}
          pagination={{ pageSize: 25 }}
          columns={[
            { title: t('code'), dataIndex: 'code', width: 100 },
            { title: t('name'), dataIndex: 'name' },
            { title: t('capacity'), dataIndex: 'capacity', width: 90, render: (v) => v ?? '—' },
            { title: t('stops'), dataIndex: 'stop_count', width: 80 },
            { title: t('activeRiders'), dataIndex: 'active_riders', width: 100 },
            {
              title: t('status'),
              dataIndex: 'is_active',
              width: 100,
              render: (v) =>
                v === 0 || v === false ? <Tag>{t('inactive')}</Tag> : <Tag color="success">{t('active')}</Tag>,
            },
            {
              title: t('actions'),
              width: 200,
              render: (_: unknown, row: TransportRoute) => (
                <Space>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => navigate(`/education/transport/routes/${row.id}`)}
                  >
                    {t('open')}
                  </Button>
                  {canManage ? (
                    <Popconfirm
                      title={t('transportRouteDeleteConfirm')}
                      onConfirm={() => remove.mutate(row.id)}
                    >
                      <Button size="small" danger loading={remove.isPending}>
                        {tc('delete')}
                      </Button>
                    </Popconfirm>
                  ) : null}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={t('newTransportRoute')}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={(values) => create.mutate(values)}>
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="capacity" label={t('capacity')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="vehicle_label" label={t('vehicleLabel')}>
            <Input />
          </Form.Item>
          <Form.Item name="is_active" label={t('active')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>
    </EducationPageChrome>
  );
}
